// FormSettingsExternalReport — the "External report" tab of the Form settings
// drawer: push each completed submission to an external API. One destination
// card (endpoint + method + auth) with four collapsible bind sections mapping
// form fields / fixed values / system metadata / field media onto payload
// columns, and an enable switch gated on the required auth fields. Saves via
// PATCH /forms/update-api-export/:id through the drawer's imperative handle.
//
// The wire shape keys bindings by fieldId (element), system-field id (system)
// or column key (custom — the only stable unique string a custom row has); the
// UI edits ordered row arrays per section and converts at the seed/save
// boundary. One column-key namespace spans all four sections, so Add is gated
// on key uniqueness — which also keeps the custom rows' map keys collision-safe.

import { forwardRef, useImperativeHandle, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Chip,
  Field,
  Icon,
  IconButton,
  Input,
  SelectDropdown,
  Switch,
} from '@/components/common';
import {
  API_EXPORT_DATA_TYPES,
  useUpdateApiExportMutation,
  type ApiExportAuthType,
  type ApiExportBinding,
  type ApiExportDataType,
  type ApiExportMediaBinding,
  type ApiExportMethod,
  type FormDesign,
  type UpdateApiExportPayload,
} from '@/api/endpoints/formsApi';
import { seedPagesFromDesign } from '../utils/builderPayload';
import { FIELD_LIBRARY } from '../utils/fieldLibrary';
import type { FormSettingsTabHandle } from './FormSettingsDrawer';

interface FormSettingsExternalReportProps {
  /** The form being edited (PATCH target). */
  formId: string;
  /** Saved settings from the form detail (the tab remounts each open). */
  initial?: UpdateApiExportPayload | null;
  /** The saved design — source of the Field ID dropdowns. */
  design?: FormDesign;
}

// One UI row per bind section; converted to/from the keyed wire maps.
interface ElementRow {
  fieldId: string;
  key: string;
  dataType: ApiExportDataType;
}
interface CustomRow {
  value: string;
  key: string;
  dataType: ApiExportDataType;
}
interface SystemRow {
  fieldId: string;
  key: string;
  dataType: ApiExportDataType;
}
interface MediaRow {
  fieldId: string;
  key: string;
  allowMultiple: boolean;
}

interface FieldOption {
  id: string;
  title: string;
}

/** Submission metadata the system section can bind — ids are the wire values. */
const SYSTEM_FIELDS = [
  { fieldId: 'user_name', labelKey: 'form_external_sys_user_name' },
  { fieldId: 'submit_date', labelKey: 'form_external_sys_submit_date' },
  { fieldId: 'submission_status', labelKey: 'form_external_sys_submission_status' },
  { fieldId: 'form_name', labelKey: 'form_external_sys_form_name' },
  { fieldId: 'reference_number', labelKey: 'form_external_sys_reference_number' },
] as const;

// Field ID pools — the watermark-mention exclusion idiom. Media stays wider
// than the answer pool: any answerable field can capture media through
// require_picture/video/note triggers, so only the never-answerable rows drop.
const FIELD_EXCLUDED = new Set(['section', 'image', 'dataSource']);
const MEDIA_EXCLUDED = new Set(['section', 'dataSource']);

/** Collapsible gray sub-card: title + description head, count chip, chevron. */
const BindCard = ({
  title,
  desc,
  count,
  children,
}: {
  title: string;
  desc: string;
  count: number;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <section className='form-external__bind'>
      <button
        type='button'
        className='form-external__bind-head'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className='form-external__bind-text'>
          <span className='form-external__bind-title'>{title}</span>
          <span className='form-external__bind-desc'>{desc}</span>
        </span>
        {/* Bare count — data, not copy. */}
        {count > 0 && (
          <Chip variant='success' size='md'>
            {count}
          </Chip>
        )}
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
      </button>
      {open && <div className='form-external__bind-body'>{children}</div>}
    </section>
  );
};

/** Label + control cell of a draft row (the drafts label each input inline). */
const DraftCell = ({ label, children }: { label: string; children: ReactNode }) => (
  <span className='form-external__draft-cell'>
    <span className='form-external__draft-label'>{label}</span>
    {children}
  </span>
);

const FormSettingsExternalReport = forwardRef<
  FormSettingsTabHandle,
  FormSettingsExternalReportProps
>(function FormSettingsExternalReport({ formId, initial, design }, ref) {
  const { t } = useTranslation();

  // ---- state, seeded from the saved setting (remount-per-open resets) ----
  const [cardOpen, setCardOpen] = useState(true);
  const [endpoint, setEndpoint] = useState(initial?.apiEndpoint ?? '');
  const [method, setMethod] = useState<ApiExportMethod>(initial?.apiMethod ?? 'post');
  const [authType, setAuthType] = useState<ApiExportAuthType>(initial?.auth.authType ?? 'bearer');
  const [token, setToken] = useState(initial?.auth.authValue ?? '');
  const [authUrl, setAuthUrl] = useState(initial?.auth.authApiUrl ?? '');
  const [authMethod, setAuthMethod] = useState<ApiExportMethod>(
    initial?.auth.authApiMethod ?? 'post',
  );
  const [clientName, setClientName] = useState(initial?.auth.parameters.client_name ?? '');
  const [clientId, setClientId] = useState(initial?.auth.parameters.client_id ?? '');
  const [clientSecret, setClientSecret] = useState(initial?.auth.parameters.client_secret ?? '');
  const [responseKey, setResponseKey] = useState(initial?.auth.responseAuthKeyName ?? '');
  const [enabled, setEnabled] = useState(initial?.isEnabled ?? false);
  // Gate error display until the first failed save (the Setup-tab idiom).
  const [submitted, setSubmitted] = useState(false);

  // Partition the keyed wire map into the per-section row arrays.
  const [elementRows, setElementRows] = useState<ElementRow[]>(() =>
    Object.entries(initial?.bindingList ?? {})
      .filter(([, b]) => b.type === 'element')
      .map(([fieldId, b]) => ({ fieldId, key: b.key, dataType: b.dataType })),
  );
  const [systemRows, setSystemRows] = useState<SystemRow[]>(() =>
    Object.entries(initial?.bindingList ?? {})
      .filter(([, b]) => b.type === 'system')
      .map(([fieldId, b]) => ({ fieldId, key: b.key, dataType: b.dataType })),
  );
  const [customRows, setCustomRows] = useState<CustomRow[]>(() =>
    Object.values(initial?.bindingList ?? {})
      .filter((b) => b.type === 'custom')
      .map((b) => ({ value: b.customValue ?? '', key: b.key, dataType: b.dataType })),
  );
  const [mediaRows, setMediaRows] = useState<MediaRow[]>(() =>
    Object.entries(initial?.mediaBindingList ?? {}).map(([fieldId, b]) => ({
      fieldId,
      key: b.key,
      allowMultiple: b.allowMultiple,
    })),
  );

  const [updateApiExport] = useUpdateApiExportMutation();

  // ---- Field ID pools from the saved design ----
  const pages = useMemo(() => (design ? seedPagesFromDesign(design.elements) : []), [design]);
  const [fieldOptions, mediaOptions] = useMemo(() => {
    const derive = (excluded: Set<string>): FieldOption[] =>
      pages.flatMap((page) =>
        page.fields
          .filter((f) => !excluded.has(f.elementType))
          .flatMap((f) => {
            // The wire id (saved designs always carry elementGlobalId).
            const id = f.elementGlobalId ?? f.uniqueIdToReplace;
            if (!id) return [];
            const bp = f.properties.basicProperties as Record<string, unknown>;
            const entry = FIELD_LIBRARY.find((e) => e.type === f.elementType);
            return [
              {
                id,
                title: (bp.fieldTitle as string) || (entry ? t(entry.labelKey) : f.elementType),
              },
            ];
          }),
      );
    return [derive(FIELD_EXCLUDED), derive(MEDIA_EXCLUDED)];
  }, [pages, t]);

  // A deleted-since-save field still renders honestly (raw id) so the user can
  // see and remove the stale binding rather than it being silently dropped.
  const fieldTitle = (id: string) =>
    fieldOptions.find((f) => f.id === id)?.title ??
    mediaOptions.find((f) => f.id === id)?.title ??
    id;
  const systemTitle = (id: string) => {
    const entry = SYSTEM_FIELDS.find((s) => s.fieldId === id);
    return entry ? t(entry.labelKey) : id;
  };

  // One column-key namespace across all four sections.
  const takenKeys = useMemo(
    () => new Set([...elementRows, ...customRows, ...systemRows, ...mediaRows].map((r) => r.key)),
    [elementRows, customRows, systemRows, mediaRows],
  );

  const dataTypeOptions = API_EXPORT_DATA_TYPES.map((v) => ({
    value: v,
    label: t(`form_external_type_${v}`),
  }));
  const selectPlaceholder = { value: '', label: t('form_external_select_ph') };

  // ---- drafts (one per section) ----
  const [draftField, setDraftField] = useState({ fieldId: '', key: '', dataType: '' });
  const [draftCustom, setDraftCustom] = useState({ value: '', key: '', dataType: '' });
  const [draftSystem, setDraftSystem] = useState({ fieldId: '', key: '', dataType: '' });
  const [draftMedia, setDraftMedia] = useState({ fieldId: '', key: '', allowMultiple: false });

  const keyFree = (key: string) => Boolean(key.trim()) && !takenKeys.has(key.trim());

  // ---- enable gating: endpoint + the active auth variant's required field ----
  const isApiAuth = authType === 'api_auth';
  const canEnable =
    endpoint.trim() !== '' && (isApiAuth ? authUrl.trim() !== '' : token.trim() !== '');

  const endpointError =
    submitted && !endpoint.trim() ? t('form_external_endpoint_required') : undefined;
  const tokenError =
    submitted && !isApiAuth && !token.trim() ? t('form_external_token_required') : undefined;
  const authUrlError =
    submitted && isApiAuth && !authUrl.trim() ? t('form_external_auth_url_required') : undefined;

  useImperativeHandle(ref, () => ({
    save: async () => {
      setSubmitted(true);
      if (!endpoint.trim() || (isApiAuth ? !authUrl.trim() : !token.trim())) return false;

      const bindingList: Record<string, ApiExportBinding> = {};
      for (const r of elementRows)
        bindingList[r.fieldId] = { key: r.key, dataType: r.dataType, type: 'element' };
      for (const r of systemRows)
        bindingList[r.fieldId] = { key: r.key, dataType: r.dataType, type: 'system' };
      for (const r of customRows)
        bindingList[r.key] = {
          key: r.key,
          dataType: r.dataType,
          type: 'custom',
          customValue: r.value,
        };
      const mediaBindingList: Record<string, ApiExportMediaBinding> = {};
      for (const r of mediaRows)
        mediaBindingList[r.fieldId] = {
          key: r.key,
          dataType: 'string',
          type: 'element',
          allowMultiple: r.allowMultiple,
        };

      // Only the active auth variant's fields go on the wire; the inactive
      // one's are sent cleared (all keys are always present, per contract).
      await updateApiExport({
        id: formId,
        body: {
          isEnabled: enabled && canEnable,
          apiEndpoint: endpoint.trim(),
          apiMethod: method,
          auth: {
            authType,
            authValue: isApiAuth ? '' : token,
            authApiUrl: isApiAuth ? authUrl : '',
            authApiMethod: isApiAuth ? authMethod : 'get',
            parameters: isApiAuth
              ? { client_name: clientName, client_id: clientId, client_secret: clientSecret }
              : { client_name: '', client_id: '', client_secret: '' },
            responseAuthKeyName: isApiAuth ? responseKey : '',
          },
          bindingList,
          mediaBindingList,
        },
      }).unwrap();
      return true;
    },
  }));

  // HTTP method options — bare wire markers, rendered literally.
  const methodOptions: { value: ApiExportMethod; label: string }[] = [
    { value: 'post', label: 'POST' },
    { value: 'get', label: 'GET' },
  ];
  // `custom` behaves like bearer on this screen (a pasted token) — the two
  // differ only in how the backend sends the token on.
  const authTypeOptions: { value: ApiExportAuthType; label: string }[] = [
    { value: 'bearer', label: t('form_external_auth_bearer') },
    { value: 'api_auth', label: t('form_external_auth_api') },
    { value: 'custom', label: t('form_external_auth_custom') },
  ];

  const boundFieldIds = new Set(elementRows.map((r) => r.fieldId));
  const boundSystemIds = new Set(systemRows.map((r) => r.fieldId));
  const boundMediaIds = new Set(mediaRows.map((r) => r.fieldId));

  return (
    <div className='form-external'>
      <p className='form-external__intro'>{t('form_external_intro')}</p>

      <section className='form-external__card'>
        <button
          type='button'
          className='form-external__card-head'
          aria-expanded={cardOpen}
          onClick={() => setCardOpen((v) => !v)}
        >
          <span className='form-external__card-icon' aria-hidden='true'>
            <Icon name='webhook' size={17} />
          </span>
          <span className='form-external__card-title'>{t('form_external_api_card_title')}</span>
          <span className='form-external__card-info' title={t('form_external_api_card_info')}>
            <Icon name='info' size={15} />
          </span>
          <Icon name={cardOpen ? 'chevron-up' : 'chevron-down'} size={16} />
        </button>

        {cardOpen && (
          <div className='form-external__body'>
            <Field label={t('form_external_endpoint_label')} required error={endpointError}>
              <Input
                size='sm'
                value={endpoint}
                error={Boolean(endpointError)}
                placeholder={t('form_external_endpoint_ph')}
                onChange={(e) => setEndpoint(e.target.value)}
              />
            </Field>

            <div className='form-external__grid'>
              <Field label={t('form_external_method_label')}>
                <SelectDropdown
                  aria-label={t('form_external_method_label')}
                  value={method}
                  options={methodOptions}
                  onChange={(next) => setMethod(next)}
                  portal
                />
              </Field>
              <Field label={t('form_external_auth_type_label')}>
                <SelectDropdown
                  aria-label={t('form_external_auth_type_label')}
                  value={authType}
                  options={authTypeOptions}
                  onChange={(next) => setAuthType(next)}
                  portal
                />
              </Field>
            </div>

            {!isApiAuth ? (
              <Field label={t('form_external_token_label')} required error={tokenError}>
                <Input
                  size='sm'
                  value={token}
                  error={Boolean(tokenError)}
                  placeholder={t('form_external_token_ph')}
                  onChange={(e) => setToken(e.target.value)}
                />
              </Field>
            ) : (
              <div className='form-external__auth-panel'>
                <Field label={t('form_external_auth_url_label')} required error={authUrlError}>
                  <Input
                    size='sm'
                    value={authUrl}
                    error={Boolean(authUrlError)}
                    placeholder={t('form_external_auth_url_ph')}
                    onChange={(e) => setAuthUrl(e.target.value)}
                  />
                </Field>
                <Field label={t('form_external_auth_method_label')}>
                  <SelectDropdown
                    aria-label={t('form_external_auth_method_label')}
                    value={authMethod}
                    options={methodOptions}
                    onChange={(next) => setAuthMethod(next)}
                    portal
                  />
                </Field>
                <Field label={t('form_external_client_name_label')}>
                  <Input
                    size='sm'
                    value={clientName}
                    placeholder={t('form_external_client_name_ph')}
                    onChange={(e) => setClientName(e.target.value)}
                  />
                </Field>
                <Field label={t('form_external_client_id_label')}>
                  <Input
                    size='sm'
                    value={clientId}
                    placeholder={t('form_external_client_id_ph')}
                    onChange={(e) => setClientId(e.target.value)}
                  />
                </Field>
                <Field label={t('form_external_client_secret_label')}>
                  <Input
                    size='sm'
                    value={clientSecret}
                    placeholder={t('form_external_client_secret_ph')}
                    onChange={(e) => setClientSecret(e.target.value)}
                  />
                </Field>
                <Field label={t('form_external_response_key_label')}>
                  <Input
                    size='sm'
                    value={responseKey}
                    placeholder={t('form_external_response_key_ph')}
                    onChange={(e) => setResponseKey(e.target.value)}
                  />
                </Field>
              </div>
            )}

            <BindCard
              title={t('form_external_bind_field_title')}
              desc={t('form_external_bind_field_desc')}
              count={elementRows.length}
            >
              <div className='form-external__draft'>
                <DraftCell label={t('form_external_col_field')}>
                  <SelectDropdown
                    size='sm'
                    aria-label={t('form_external_col_field')}
                    value={draftField.fieldId}
                    options={[
                      selectPlaceholder,
                      ...fieldOptions
                        .filter((f) => !boundFieldIds.has(f.id))
                        .map((f) => ({ value: f.id, label: f.title })),
                    ]}
                    onChange={(next) => setDraftField((d) => ({ ...d, fieldId: next }))}
                    portal
                  />
                </DraftCell>
                <DraftCell label={t('form_external_col_key')}>
                  <Input
                    size='sm'
                    value={draftField.key}
                    placeholder={t('form_external_key_ph')}
                    onChange={(e) => setDraftField((d) => ({ ...d, key: e.target.value }))}
                  />
                </DraftCell>
                <DraftCell label={t('form_external_col_type')}>
                  <SelectDropdown
                    size='sm'
                    aria-label={t('form_external_col_type')}
                    value={draftField.dataType}
                    options={[selectPlaceholder, ...dataTypeOptions]}
                    onChange={(next) => setDraftField((d) => ({ ...d, dataType: next }))}
                    portal
                  />
                </DraftCell>
                <Button
                  size='sm'
                  className='form-external__draft-add'
                  disabled={!draftField.fieldId || !draftField.dataType || !keyFree(draftField.key)}
                  onClick={() => {
                    setElementRows((rows) => [
                      ...rows,
                      {
                        fieldId: draftField.fieldId,
                        key: draftField.key.trim(),
                        dataType: draftField.dataType as ApiExportDataType,
                      },
                    ]);
                    setDraftField({ fieldId: '', key: '', dataType: '' });
                  }}
                >
                  {t('form_external_add')}
                </Button>
              </div>

              {elementRows.length > 0 && (
                <div className='form-external__table'>
                  <div className='form-external__row form-external__row--head' aria-hidden='true'>
                    <span>{t('form_external_col_field')}</span>
                    <span>{t('form_external_col_key')}</span>
                    <span>{t('form_external_col_type')}</span>
                    <span />
                  </div>
                  {elementRows.map((r) => (
                    <div key={r.key} className='form-external__row'>
                      <span className='form-external__cell' title={fieldTitle(r.fieldId)}>
                        {fieldTitle(r.fieldId)}
                      </span>
                      <span className='form-external__cell' title={r.key}>
                        {r.key}
                      </span>
                      <span className='form-external__cell'>
                        {t(`form_external_type_${r.dataType}`)}
                      </span>
                      <IconButton
                        icon='trash-2'
                        variant='ghost'
                        size='sm'
                        className='form-external__remove'
                        aria-label={t('form_external_remove')}
                        onClick={() =>
                          setElementRows((rows) => rows.filter((x) => x.key !== r.key))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </BindCard>

            <BindCard
              title={t('form_external_bind_custom_title')}
              desc={t('form_external_bind_custom_desc')}
              count={customRows.length}
            >
              <div className='form-external__draft'>
                <DraftCell label={t('form_external_col_value')}>
                  <Input
                    size='sm'
                    value={draftCustom.value}
                    placeholder={t('form_external_value_ph')}
                    onChange={(e) => setDraftCustom((d) => ({ ...d, value: e.target.value }))}
                  />
                </DraftCell>
                <DraftCell label={t('form_external_col_key')}>
                  <Input
                    size='sm'
                    value={draftCustom.key}
                    placeholder={t('form_external_key_ph')}
                    onChange={(e) => setDraftCustom((d) => ({ ...d, key: e.target.value }))}
                  />
                </DraftCell>
                <DraftCell label={t('form_external_col_type')}>
                  <SelectDropdown
                    size='sm'
                    aria-label={t('form_external_col_type')}
                    value={draftCustom.dataType}
                    options={[selectPlaceholder, ...dataTypeOptions]}
                    onChange={(next) => setDraftCustom((d) => ({ ...d, dataType: next }))}
                    portal
                  />
                </DraftCell>
                <Button
                  size='sm'
                  className='form-external__draft-add'
                  disabled={
                    !draftCustom.value.trim() || !draftCustom.dataType || !keyFree(draftCustom.key)
                  }
                  onClick={() => {
                    setCustomRows((rows) => [
                      ...rows,
                      {
                        value: draftCustom.value.trim(),
                        key: draftCustom.key.trim(),
                        dataType: draftCustom.dataType as ApiExportDataType,
                      },
                    ]);
                    setDraftCustom({ value: '', key: '', dataType: '' });
                  }}
                >
                  {t('form_external_add')}
                </Button>
              </div>

              {customRows.length > 0 && (
                <div className='form-external__table'>
                  <div className='form-external__row form-external__row--head' aria-hidden='true'>
                    <span>{t('form_external_col_value')}</span>
                    <span>{t('form_external_col_key')}</span>
                    <span>{t('form_external_col_type')}</span>
                    <span />
                  </div>
                  {customRows.map((r) => (
                    <div key={r.key} className='form-external__row'>
                      <span className='form-external__cell' title={r.value}>
                        {r.value}
                      </span>
                      <span className='form-external__cell' title={r.key}>
                        {r.key}
                      </span>
                      <span className='form-external__cell'>
                        {t(`form_external_type_${r.dataType}`)}
                      </span>
                      <IconButton
                        icon='trash-2'
                        variant='ghost'
                        size='sm'
                        className='form-external__remove'
                        aria-label={t('form_external_remove')}
                        onClick={() => setCustomRows((rows) => rows.filter((x) => x.key !== r.key))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </BindCard>

            <BindCard
              title={t('form_external_bind_system_title')}
              desc={t('form_external_bind_system_desc')}
              count={systemRows.length}
            >
              <div className='form-external__draft'>
                <DraftCell label={t('form_external_col_field')}>
                  <SelectDropdown
                    size='sm'
                    aria-label={t('form_external_col_field')}
                    value={draftSystem.fieldId}
                    options={[
                      selectPlaceholder,
                      ...SYSTEM_FIELDS.filter((s) => !boundSystemIds.has(s.fieldId)).map((s) => ({
                        value: s.fieldId,
                        label: t(s.labelKey),
                      })),
                    ]}
                    onChange={(next) => setDraftSystem((d) => ({ ...d, fieldId: next }))}
                    portal
                  />
                </DraftCell>
                <DraftCell label={t('form_external_col_key')}>
                  <Input
                    size='sm'
                    value={draftSystem.key}
                    placeholder={t('form_external_key_ph')}
                    onChange={(e) => setDraftSystem((d) => ({ ...d, key: e.target.value }))}
                  />
                </DraftCell>
                <DraftCell label={t('form_external_col_type')}>
                  <SelectDropdown
                    size='sm'
                    aria-label={t('form_external_col_type')}
                    value={draftSystem.dataType}
                    options={[selectPlaceholder, ...dataTypeOptions]}
                    onChange={(next) => setDraftSystem((d) => ({ ...d, dataType: next }))}
                    portal
                  />
                </DraftCell>
                <Button
                  size='sm'
                  className='form-external__draft-add'
                  disabled={
                    !draftSystem.fieldId || !draftSystem.dataType || !keyFree(draftSystem.key)
                  }
                  onClick={() => {
                    setSystemRows((rows) => [
                      ...rows,
                      {
                        fieldId: draftSystem.fieldId,
                        key: draftSystem.key.trim(),
                        dataType: draftSystem.dataType as ApiExportDataType,
                      },
                    ]);
                    setDraftSystem({ fieldId: '', key: '', dataType: '' });
                  }}
                >
                  {t('form_external_add')}
                </Button>
              </div>

              {systemRows.length > 0 && (
                <div className='form-external__table'>
                  <div className='form-external__row form-external__row--head' aria-hidden='true'>
                    <span>{t('form_external_col_field')}</span>
                    <span>{t('form_external_col_key')}</span>
                    <span>{t('form_external_col_type')}</span>
                    <span />
                  </div>
                  {systemRows.map((r) => (
                    <div key={r.key} className='form-external__row'>
                      <span className='form-external__cell' title={systemTitle(r.fieldId)}>
                        {systemTitle(r.fieldId)}
                      </span>
                      <span className='form-external__cell' title={r.key}>
                        {r.key}
                      </span>
                      <span className='form-external__cell'>
                        {t(`form_external_type_${r.dataType}`)}
                      </span>
                      <IconButton
                        icon='trash-2'
                        variant='ghost'
                        size='sm'
                        className='form-external__remove'
                        aria-label={t('form_external_remove')}
                        onClick={() => setSystemRows((rows) => rows.filter((x) => x.key !== r.key))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </BindCard>

            <BindCard
              title={t('form_external_bind_media_title')}
              desc={t('form_external_bind_media_desc')}
              count={mediaRows.length}
            >
              <div className='form-external__draft form-external__draft--media'>
                <DraftCell label={t('form_external_col_field')}>
                  <SelectDropdown
                    size='sm'
                    aria-label={t('form_external_col_field')}
                    value={draftMedia.fieldId}
                    options={[
                      selectPlaceholder,
                      ...mediaOptions
                        .filter((f) => !boundMediaIds.has(f.id))
                        .map((f) => ({ value: f.id, label: f.title })),
                    ]}
                    onChange={(next) => setDraftMedia((d) => ({ ...d, fieldId: next }))}
                    portal
                  />
                </DraftCell>
                <DraftCell label={t('form_external_col_key')}>
                  <Input
                    size='sm'
                    value={draftMedia.key}
                    placeholder={t('form_external_key_ph')}
                    onChange={(e) => setDraftMedia((d) => ({ ...d, key: e.target.value }))}
                  />
                </DraftCell>
                <Checkbox
                  size='sm'
                  className='form-external__draft-check'
                  label={t('form_external_col_multiple')}
                  checked={draftMedia.allowMultiple}
                  onChange={(e) =>
                    setDraftMedia((d) => ({ ...d, allowMultiple: e.target.checked }))
                  }
                />
                <Button
                  size='sm'
                  className='form-external__draft-add'
                  disabled={!draftMedia.fieldId || !keyFree(draftMedia.key)}
                  onClick={() => {
                    setMediaRows((rows) => [
                      ...rows,
                      {
                        fieldId: draftMedia.fieldId,
                        key: draftMedia.key.trim(),
                        allowMultiple: draftMedia.allowMultiple,
                      },
                    ]);
                    setDraftMedia({ fieldId: '', key: '', allowMultiple: false });
                  }}
                >
                  {t('form_external_add')}
                </Button>
              </div>

              {mediaRows.length > 0 && (
                <div className='form-external__table'>
                  <div className='form-external__row form-external__row--head' aria-hidden='true'>
                    <span>{t('form_external_col_field')}</span>
                    <span>{t('form_external_col_key')}</span>
                    <span>{t('form_external_col_multiple')}</span>
                    <span />
                  </div>
                  {mediaRows.map((r) => (
                    <div key={r.key} className='form-external__row'>
                      <span className='form-external__cell' title={fieldTitle(r.fieldId)}>
                        {fieldTitle(r.fieldId)}
                      </span>
                      <span className='form-external__cell' title={r.key}>
                        {r.key}
                      </span>
                      <span className='form-external__cell'>
                        {r.allowMultiple ? t('text_yes') : t('text_no')}
                      </span>
                      <IconButton
                        icon='trash-2'
                        variant='ghost'
                        size='sm'
                        className='form-external__remove'
                        aria-label={t('form_external_remove')}
                        onClick={() => setMediaRows((rows) => rows.filter((x) => x.key !== r.key))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </BindCard>

            <div className='form-external__enable'>
              <div className='form-external__enable-text'>
                <span className='form-external__enable-label'>{t('form_external_enable')}</span>
                {!canEnable && (
                  <span className='form-external__enable-hint'>
                    <Icon name='info' size={14} aria-hidden='true' />
                    {t('form_external_enable_hint')}
                  </span>
                )}
              </div>
              <Switch
                checked={enabled && canEnable}
                disabled={!canEnable}
                aria-label={t('form_external_enable')}
                onChange={() => setEnabled((v) => !v)}
              />
            </div>
          </div>
        )}
      </section>
    </div>
  );
});

export default FormSettingsExternalReport;
